import { MediaTypes } from '../commands/practitioner/enums/MediaTypes';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.avif', '.heic', '.heif'];
const AUDIO_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.ogg',
  '.oga',
  '.weba',
  '.aac',
  '.m4a',
  '.m4b',
  '.mpga',
  '.mp2',
  '.mp2a',
  '.m2a',
  '.m3a',
  '.spx',
];
const VIDEO_EXTENSIONS = ['.mp4', '.ogv', '.webm', '.mov', '.m4v'];

const getNormalizedMime = (mime?: string | null) => mime?.trim().toLowerCase() ?? '';

const getNormalizedExtension = (value?: string | null) => {
  if (!value?.trim()) {
    return '';
  }

  const normalizedValue = value.trim().toLowerCase().split(/[?#]/, 1)[0];
  const dotIndex = normalizedValue.lastIndexOf('.');
  const slashIndex = Math.max(normalizedValue.lastIndexOf('/'), normalizedValue.lastIndexOf('\\'));

  if (dotIndex === -1 || dotIndex < slashIndex) {
    return '';
  }

  return normalizedValue.slice(dotIndex);
};

export const detectMediaTypeFromMime = (mime?: string | null): MediaTypes | null => {
  const normalizedMime = getNormalizedMime(mime);

  if (!normalizedMime) {
    return null;
  }

  if (normalizedMime === 'application/ogg') {
    return MediaTypes.AUDIO;
  }

  if (normalizedMime.startsWith('video/')) {
    return MediaTypes.VIDEO;
  }

  if (normalizedMime.startsWith('image/')) {
    return MediaTypes.IMAGE;
  }

  if (normalizedMime.startsWith('audio/')) {
    return MediaTypes.AUDIO;
  }

  return null;
};

export const detectMediaTypeFromExtension = (value?: string | null): MediaTypes | null => {
  const extension = getNormalizedExtension(value);

  if (!extension) {
    return null;
  }

  if (IMAGE_EXTENSIONS.includes(extension)) {
    return MediaTypes.IMAGE;
  }

  if (AUDIO_EXTENSIONS.includes(extension)) {
    return MediaTypes.AUDIO;
  }

  if (VIDEO_EXTENSIONS.includes(extension)) {
    return MediaTypes.VIDEO;
  }

  return null;
};

export const detectMediaTypeFromFile = (file: Pick<File, 'name' | 'type'>): MediaTypes | null => {
  const normalizedMime = getNormalizedMime(file.type);

  if (normalizedMime === 'application/ogg') {
    return detectMediaTypeFromExtension(file.name) ?? MediaTypes.AUDIO;
  }

  return detectMediaTypeFromMime(file.type) ?? detectMediaTypeFromExtension(file.name);
};

export const detectMediaTypeFromUrl = (value?: string | null): MediaTypes | null => {
  const mediaType = detectMediaTypeFromExtension(value);

  if (mediaType !== null) {
    return mediaType;
  }

  if (!value?.trim()) {
    return null;
  }

  try {
    const parsedUrl = new URL(value);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return MediaTypes.VIDEO;
    }

    if (hostname.includes('soundcloud.com') || hostname.includes('spotify.com')) {
      return MediaTypes.AUDIO;
    }

    if (hostname.includes('google.com') && parsedUrl.pathname.includes('/search')) {
      return MediaTypes.IMAGE;
    }
  } catch (error) {
    return null;
  }

  return null;
};

export const isWavOggFile = (file: Pick<File, 'name' | 'type'>): boolean => {
  const extension = getNormalizedExtension(file.name);
  return ['.wav', '.ogg', '.oga'].includes(extension);
};

export const isGifFile = (file: Pick<File, 'name' | 'type'>): boolean => {
  const extension = getNormalizedExtension(file.name);
  const mime = getNormalizedMime(file.type);
  return extension === '.gif' || mime === 'image/gif';
};

export const isGifUrl = (value?: string | null): boolean => {
  const extension = getNormalizedExtension(value);
  return extension === '.gif';
};

export const isHeicLikeFile = (file: Pick<File, 'name' | 'type'>) => {
  const normalizedMime = getNormalizedMime(file.type);

  if (normalizedMime === 'image/heic' || normalizedMime === 'image/heif') {
    return true;
  }

  const extension = getNormalizedExtension(file.name);
  return extension === '.heic' || extension === '.heif';
};
